import { ChevronLeft, ChevronRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

import { useTableContext } from './TableContext'

export const Navigation = () => {
  // Use the context
  const {
    currentPage,
    setCurrentPage,
    pageSize,
    purchaseOrderData,
    isLoading,
    goToNextPage,
    goToPreviousPage,
  } = useTableContext()

  // Get the isFetching state from the context
  const isFetching = isLoading

  // Determine what pagination buttons to show
  const renderPaginationButtons = () => {
    if (!purchaseOrderData?.totalPages) return null

    const totalPages = purchaseOrderData.totalPages

    // For mobile screens, show fewer page buttons
    const isMobile = window.innerWidth < 640
    const maxButtonsToShow = isMobile ? 3 : 7

    let startPage = Math.max(1, currentPage - Math.floor(maxButtonsToShow / 2))
    const endPage = Math.min(totalPages, startPage + maxButtonsToShow - 1)

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxButtonsToShow) {
      startPage = Math.max(1, endPage - maxButtonsToShow + 1)
    }

    const buttons = []

    // Add first page if not visible
    if (startPage > 1) {
      buttons.push(
        <Button
          className="h-8 w-8 hover:bg-accent hover:text-accent-foreground"
          disabled={isFetching}
          key={1}
          onClick={() => setCurrentPage(1)}
          size="icon"
          variant={1 === currentPage ? 'default' : 'outline'}
        >
          1
        </Button>
      )

      if (startPage > 2) {
        buttons.push(
          <span className="px-2 text-muted-foreground" key="ellipsis-start">
            ...
          </span>
        )
      }
    }

    // Add visible page buttons
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button
          className="h-8 w-8 hover:bg-accent hover:text-accent-foreground"
          disabled={isFetching}
          key={i}
          onClick={() => setCurrentPage(i)}
          size="icon"
          variant={i === currentPage ? 'default' : 'outline'}
        >
          {i}
        </Button>
      )
    }

    // Add last page if not visible
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span className="px-2 text-muted-foreground" key="ellipsis-end">
            ...
          </span>
        )
      }

      buttons.push(
        <Button
          className="h-8 w-8 hover:bg-accent hover:text-accent-foreground"
          disabled={isFetching}
          key={totalPages}
          onClick={() => setCurrentPage(totalPages)}
          size="icon"
          variant={totalPages === currentPage ? 'default' : 'outline'}
        >
          {totalPages}
        </Button>
      )
    }

    return buttons
  }

  // Render skeleton pagination while loading
  const renderSkeletonPagination = () => {
    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton className="h-8 w-8" key={index} />
        ))}
      </div>
    )
  }

  return (
    <nav
      aria-label="Paginación"
      className="flex flex-col items-center justify-between gap-2 border-t px-4 py-4 sm:flex-row"
    >
      <div className="order-2 min-w-[300px] text-center text-sm text-muted-foreground sm:order-1 sm:text-left">
        {isFetching ? (
          <Skeleton className="h-5 w-56" />
        ) : (
          <>
            Mostrando {(currentPage - 1) * pageSize + 1} al{' '}
            {Math.min(
              currentPage * pageSize,
              purchaseOrderData?.totalItems || 0
            )}{' '}
            de {purchaseOrderData?.totalItems || 0} resultados
          </>
        )}
      </div>

      <div className="order-1 flex items-center gap-1 sm:order-2">
        <Button
          aria-label="Página anterior"
          className="h-8 w-8 hover:bg-accent hover:text-accent-foreground"
          disabled={isFetching || !purchaseOrderData || currentPage <= 1}
          onClick={goToPreviousPage}
          size="icon"
          variant="outline"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {isFetching ? (
          renderSkeletonPagination()
        ) : (
          <div className="flex items-center">{renderPaginationButtons()}</div>
        )}

        <Button
          aria-label="Página siguiente"
          className="h-8 w-8 hover:bg-accent hover:text-accent-foreground"
          disabled={
            isFetching ||
            !purchaseOrderData ||
            currentPage >= (purchaseOrderData.totalPages || 1)
          }
          onClick={goToNextPage}
          size="icon"
          variant="outline"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </nav>
  )
}
